import { Box, Card, CardContent, Chip, Divider, Stack, ToggleButton, ToggleButtonGroup, Tooltip, Typography } from '@mui/material';
import { useGetRecentTasksQuery, useGetTasksQuery } from '../../services/tasks.service';
import { CHIPS_COLOR } from '../../types/tasks';
import moment from 'moment';
import { Task, ArrowForward, AccessTime } from '@mui/icons-material';
import { useAppTheme } from '../../context/ThemeContext';
import { Link } from 'react-router-dom';
import { useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';

function TasksCard() {
  const { data: recentData } = useGetRecentTasksQuery('Tasks');
  const { data: allData } = useGetTasksQuery('Tasks');
  const { isNewTheme } = useAppTheme();
  const currentUserId = useSelector((state: RootState) => state.user._id);
  const [view, setView] = useState<"all" | "mine">("all");

  const now = Date.now();

  const data = view === "mine"
    ? (allData ?? []).filter((t) => t.assignedTo === currentUserId).slice(0, 10)
    : recentData;

  const overdueCount = (data ?? []).filter(
    (t) => t.status !== "Completed" && t.dueDate && new Date(t.dueDate as any).getTime() < now
  ).length;

  return (
    <Card
      sx={
        isNewTheme
          ? {
              width: "100%",
              height: "100%",
              borderRadius: "8px",
              border: "1px solid #E2E8F0",
              boxShadow: "0 2px 6px rgba(15,23,42,0.05)",
              display: "flex",
              flexDirection: "column",
              transition: "box-shadow 0.15s ease",
              "&:hover": { boxShadow: "0 4px 16px rgba(15,23,42,0.07)" },
            }
          : {
              width: "100%",
              height: "100%",
              borderRadius: "10px",
              boxShadow: "0px 0px 55px 5px rgba(0, 0, 0, 0.05)",
              display: "flex",
              flexDirection: "column",
            }
      }>
      {/* Header */}
      <Box
        sx={{
          px: 2,
          py: 1.5,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {isNewTheme && (
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: "8px",
                backgroundColor: "rgba(15,23,42,0.06)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}>
              <Task sx={{ fontSize: 18, color: "#0F172A" }} />
            </Box>
          )}
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography variant="h6" fontWeight={isNewTheme ? 700 : 600} color={isNewTheme ? "text.primary" : "black"}>
              Recent Tasks
            </Typography>
            {overdueCount > 0 && (
              <Tooltip title={`${overdueCount} overdue`} arrow>
                <Chip
                  icon={<AccessTime sx={{ fontSize: "12px !important" }} />}
                  label={overdueCount}
                  size="small"
                  color="error"
                  sx={{ height: 20, fontSize: "0.65rem", fontWeight: 700, borderRadius: "4px", "& .MuiChip-label": { px: 0.75 } }}
                />
              </Tooltip>
            )}
          </Stack>
        </Box>
        <Stack direction="row" alignItems="center" spacing={1}>
          <ToggleButtonGroup
            size="small"
            value={view}
            exclusive
            onChange={(_, v) => v && setView(v)}
            sx={{ height: 24 }}
          >
            <ToggleButton value="all" sx={{ px: 1, fontSize: "0.65rem", fontWeight: 600, textTransform: "none", py: 0 }}>
              All
            </ToggleButton>
            <ToggleButton value="mine" sx={{ px: 1, fontSize: "0.65rem", fontWeight: 600, textTransform: "none", py: 0 }}>
              Mine
            </ToggleButton>
          </ToggleButtonGroup>
          <Link to="/tasks" style={{ textDecoration: "none" }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.3,
                color: "primary.main",
                fontSize: "0.75rem",
                fontWeight: 600,
                opacity: 0.85,
                transition: "opacity 0.15s ease",
                "&:hover": { opacity: 1 },
              }}>
              View all
              <ArrowForward sx={{ fontSize: 13 }} />
            </Box>
          </Link>
        </Stack>
      </Box>

      <Divider sx={isNewTheme ? { borderColor: "#E2E8F0" } : {}} />

      <CardContent sx={{ flex: 1, overflow: "auto", p: 1.5 }}>
        <Stack
          direction="column"
          divider={
            <Divider sx={isNewTheme ? { borderColor: "#F1F5F9" } : {}} />
          }>
          {data?.map((task) => {
            const isOverdue =
              task.status !== "Completed" &&
              task.dueDate &&
              new Date(task.dueDate as any).getTime() < now;
            return (
              <Stack
                justifyContent="space-between"
                alignItems="center"
                key={task._id}
                direction="row"
                spacing={1}
                p={1}
                sx={
                  isNewTheme
                    ? {
                        borderRadius: "8px",
                        transition: "background 0.15s ease",
                        bgcolor: isOverdue ? "#FFF5F5" : "transparent",
                        "&:hover": { backgroundColor: isOverdue ? "#FEE2E2" : "#F8FAFC" },
                      }
                    : {}
                }
              >
                <Typography
                  variant="body2"
                  fontWeight={isNewTheme ? 600 : 400}
                  color={isOverdue && isNewTheme ? "error.main" : "text.primary"}
                  sx={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", flex: 1 }}
                >
                  {task.name}
                </Typography>
                <Typography
                  variant="caption"
                  color={isOverdue ? "error" : "text.secondary"}
                  fontWeight={isOverdue ? 600 : 400}
                  sx={{ whiteSpace: "nowrap", flexShrink: 0 }}
                >
                  {moment(task.dueDate).fromNow()}
                </Typography>
                <Chip
                  size="small"
                  label={task.status}
                  color={CHIPS_COLOR[task.status]}
                  sx={{ borderRadius: isNewTheme ? "4px" : "8px", fontWeight: isNewTheme ? 600 : 400, flexShrink: 0 }}
                />
              </Stack>
            );
          })}
        </Stack>
      </CardContent>
    </Card>
  );
}

export default TasksCard;
